import { Card } from "@components/cards/card";
import { IRaffleResultScheduleResponseDTO } from "@hooks/dtos/raffles/IRaffleResultScheduleDTO";
import {
  RaffleResultScheduleStatusEnum,
  RaffleResultScheduleTypeEnum,
} from "@hooks/enums/raffle_result_schedule";
import {
  getRaffleResultScheduleStatus,
  getRaffleResultScheduleType,
} from "@hooks/functions/Raffle/resultSchedule";
import { formatName } from "@hooks/functions/formatName";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  FaCalendarDays,
  FaCircleCheck,
  FaLandmark,
  FaListOl,
  FaTicket,
  FaTrophy,
} from "react-icons/fa6";

interface IProps {
  raffleKey: string;
}

export const RaffleResultVisitor = ({ raffleKey }: IProps) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [result, setResult] = useState<IRaffleResultScheduleResponseDTO>();
  const schedule = result?.schedule;
  const scheduleType = schedule ? getRaffleResultScheduleType(schedule.type) : null;
  const scheduleStatus = schedule
    ? getRaffleResultScheduleStatus(schedule.status)
    : null;
  const winnerTicket = schedule?.winner?.ticketData;
  const winnerClient = winnerTicket?.clientData;
  const steps = schedule?.winnerResult?.steps || [];

  async function loadRaffleResultSchedule() {
    try {
      const response = await api.get(`/raffleClient/default/result/schedule`, {
        params: { key: raffleKey },
      });

      setResult(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar informações do resultado da campanha.",
        type: "error",
        theme: "dark",
      });
    }
  }

  function formatDate(value?: string | null) {
    return value ? dayjs(value).format("DD/MM/YYYY [às] HH:mm") : "-";
  }

  function getMainDateLabel() {
    if (schedule?.status === RaffleResultScheduleStatusEnum.DONE) {
      return "Processado em";
    }

    return schedule?.type === RaffleResultScheduleTypeEnum.RANDOM
      ? "Sorteio em"
      : "Criado em";
  }

  function getMainDateValue() {
    if (schedule?.status === RaffleResultScheduleStatusEnum.DONE) {
      return formatDate(schedule.processedAt);
    }

    if (schedule?.type === RaffleResultScheduleTypeEnum.RANDOM) {
      return formatDate(schedule.random?.drawAt);
    }

    return formatDate(schedule?.createdAt);
  }

  function getStepText(step: any, index: number) {
    if (typeof step === "string") return step;

    return (
      step?.messages?.["pt-br"] ||
      step?.message ||
      step?.text ||
      step?.title ||
      step?.label ||
      step?.result ||
      step?.reason ||
      step?.description ||
      `Etapa ${index + 1} processada.`
    );
  }

  function getStepMeta(step: any) {
    if (!step || typeof step === "string") return "";

    const values = [];

    if (step.order) values.push(`Ordem ${step.order}`);
    if (step.ticket) values.push(`Ticket ${step.ticket}`);
    if (step.ticketId) values.push(`Ticket ID ${step.ticketId}`);

    return values.join(" | ");
  }

  useEffect(() => {
    if (raffleKey) {
      loadRaffleResultSchedule();
    }
  }, [raffleKey]);

  if (!result?.hasSchedule || !schedule || !scheduleType || !scheduleStatus) {
    return null;
  }

  return (
    <Card className="mt-4">
      <div className="card-header">
        <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
          <div>
            <div className="card-title">Resultado da Campanha</div>
            <div className="card-category">
              Informações finais do sorteio desta campanha.
            </div>
          </div>
          <div>
            <span
              className={`badge bg-${scheduleStatus.color}`}
              title={scheduleStatus.title}
            >
              {scheduleStatus.text}
            </span>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="bg-light border rounded-3 p-3 h-100">
              <small className="text-muted d-block">Tipo</small>
              <strong title={scheduleType.title}>{scheduleType.text}</strong>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="bg-light border rounded-3 p-3 h-100">
              <small className="text-muted d-block">Status</small>
              <strong title={scheduleStatus.title}>{scheduleStatus.text}</strong>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="bg-light border rounded-3 p-3 h-100">
              <small className="text-muted d-block">
                {schedule.type === RaffleResultScheduleTypeEnum.LOTTERY
                  ? "Concurso"
                  : "Agendamento"}
              </small>
              <strong>
                {schedule.type === RaffleResultScheduleTypeEnum.LOTTERY
                  ? schedule.lottery?.contestNumber || "-"
                  : formatDate(schedule.random?.drawAt)}
              </strong>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <div className="bg-light border rounded-3 p-3 h-100">
              <small className="text-muted d-block">{getMainDateLabel()}</small>
              <strong>{getMainDateValue()}</strong>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          {schedule.type === RaffleResultScheduleTypeEnum.LOTTERY && (
            <>
              <div className="col-12 col-md-6">
                <div className="border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">
                    <FaLandmark className="me-2 text-primary" />
                    Combinação usada
                  </small>
                  <strong>{schedule.lottery?.lotteryCombination || "-"}</strong>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">
                    <FaCalendarDays className="me-2 text-primary" />
                    Número da Loteria
                  </small>
                  <strong>{schedule.lottery?.lotteryNumber || "-"}</strong>
                </div>
              </div>
            </>
          )}

          {schedule.type === RaffleResultScheduleTypeEnum.RANDOM && (
            <div className="col-12">
              <div className="border rounded-3 p-3 h-100">
                <small className="text-muted d-block">
                  <FaCalendarDays className="me-2 text-primary" />
                  Data do sorteio interno
                </small>
                <strong>{formatDate(schedule.random?.drawAt)}</strong>
              </div>
            </div>
          )}
        </div>

        {winnerTicket ? (
          <div className="border border-success rounded-3 overflow-hidden mb-3">
            <div
              className="border-bottom border-success p-3"
              style={{ backgroundColor: "#f0f9f3" }}
            >
              <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                <div>
                  <strong className="text-success">
                    <FaTrophy className="me-2" />
                    Ganhador definido
                  </strong>
                  <div className="text-muted small">
                    O sorteio foi processado e o ticket vencedor já foi
                    identificado.
                  </div>
                </div>
                <div className="text-md-end">
                  <small className="text-muted d-block">Ticket vencedor</small>
                  <strong className="badge bg-success fs-6 px-3 py-2 text-white">
                    {winnerTicket.ticket}
                  </strong>
                </div>
              </div>
            </div>

            <div className="row g-3 p-3">
              <div className="col-12 col-md-6 col-xl-5">
                <div className="bg-light border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">Ganhador</small>
                  <strong>
                    {winnerClient?.name
                      ? formatName(winnerClient.name)
                      : "Não informado"}
                  </strong>
                  {winnerClient?.atSign && (
                    <div className="text-primary fw-semibold">
                      @{winnerClient.atSign}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <div className="bg-light border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">Ordem</small>
                  <strong>{winnerTicket.order}</strong>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <div className="bg-light border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">Ticket ID</small>
                  <strong>{winnerTicket.id}</strong>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-3">
                <div className="bg-light border rounded-3 p-3 h-100">
                  <small className="text-muted d-block">Origem</small>
                  <strong>
                    {schedule.winner?.type === "l" ? "Loteria" : "Sorteio"}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="alert alert-light border mb-3">
            <strong>
              <FaTicket className="me-2" />
              Ganhador ainda não definido
            </strong>
            <div className="text-muted small">
              O sorteio está cadastrado, mas ainda não há ticket vencedor para
              exibir.
            </div>
          </div>
        )}

        {schedule.message && schedule.status !== RaffleResultScheduleStatusEnum.DONE && (
          <div className={`alert alert-${scheduleStatus.color} mb-3`}>
            {schedule.message}
          </div>
        )}

        {steps.length > 0 && (
          <div className="border rounded-3 p-3">
            <strong>
              <FaCircleCheck className="me-2 text-success" />
              Como o resultado foi definido
            </strong>
            <div className="text-muted small mb-3">
              Etapas calculadas até encontrar o ticket vencedor.
            </div>

            <div className="list-group list-group-flush">
              {steps.map((step, index) => {
                const stepMeta = getStepMeta(step);

                return (
                  <div
                    className="list-group-item px-0 d-flex align-items-start justify-content-start gap-3 text-start w-100"
                    key={`result-step-${index}`}
                  >
                    <span
                      className="badge bg-primary rounded-circle flex-shrink-0"
                      style={{ width: 24, height: 24, lineHeight: "16px" }}
                    >
                      {step?.index || index + 1}
                    </span>
                    <div className="text-start flex-grow-1">
                      <div className="fw-semibold">{getStepText(step, index)}</div>
                      {stepMeta && (
                        <small className="text-muted">
                          <FaListOl className="me-1" />
                          {stepMeta}
                        </small>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
